import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, subMonths, addMonths, subDays, isSameMonth, isToday } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronDown, Check, Crown, Trophy, Clock, Timer, Zap, Sparkles, Calendar, CalendarDays, CalendarRange } from 'lucide-react';
import { Task, FocusSession } from '@/types/task';
import { cn } from '@/lib/utils';
import { openPaymentUrl } from '@/lib/desktopBridge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMultiPeriodFocusStats, useSummaryData, StatsPeriod, SummaryPeriod } from '@/hooks/useMultiPeriodFocusStats';
import { useStatusHistory } from '@/hooks/useStatusHistory';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { LogTimeDialog } from '@/components/LogTimeDialog';
import { saveFocusSession, updateFocusSession, getFocusSessionsForTask } from '@/hooks/useFocusSessions';
import { useAuth } from '@/hooks/useAuth';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DailyNotesSection } from '@/components/DailyNotesSection';
import { FlippableStatsCard } from '@/components/FlippableStatsCard';
import { AISummaryDrawer } from '@/components/AISummaryDrawer';
import { useAISummaryCache } from '@/hooks/useAISummaryCache';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useFeature } from '@/components/FeatureGate';
import { useSubscription } from '@/hooks/useSubscription';

interface ListViewProps {
  tasks: Task[];
}

interface TaskTimeMap {
  [taskId: string]: number;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

const PERIOD_CYCLE: StatsPeriod[] = ['today', 'week', 'month'];

export function ListView({ tasks }: ListViewProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showSummaryDrawer, setShowSummaryDrawer] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [summaryPeriod, setSummaryPeriod] = useState<SummaryPeriod>('week');
  const [summaryPeriodLabel, setSummaryPeriodLabel] = useState<string>('');
  const [summaryCachedAt, setSummaryCachedAt] = useState<string | null>(null);
  const [forceRegenerate, setForceRegenerate] = useState(false);
  const [showLogTimeDialog, setShowLogTimeDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [logTimeExistingSessions, setLogTimeExistingSessions] = useState<FocusSession[]>([]);
  const [taskTimeMap, setTaskTimeMap] = useState<TaskTimeMap>({});
  const [cardPeriod, setCardPeriod] = useState<StatsPeriod>('today');
  const [showPeriodPicker, setShowPeriodPicker] = useState(false);
  const { user } = useAuth();
  const { upgradeToPro, tier, isProUser } = useSubscription();
  const hasUnlimitedHistory = useFeature('unlimitedHistory');
  // Free users see last 7 days only; data beyond that is stored but hidden until upgrade. No overlay.
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Collapsible state: only today's date is expanded by default
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => new Set([todayStr]));
  
  const toggleDayExpanded = (dateStr: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  // Free: limit to last 7 days (today + previous 6 days)
  const displayTasks = useMemo(() => {
    if (hasUnlimitedHistory) return tasks;
    const cutoff = format(subDays(new Date(), 6), 'yyyy-MM-dd');
    return tasks.filter((t) => t.day && t.day >= cutoff);
  }, [tasks, hasUnlimitedHistory]);

  // Get completed tasks for the current month
  const completedTasks = useMemo(() => {
    return displayTasks.filter((t) => {
      const taskDate = new Date(t.day);
      return t.completed && isSameMonth(taskDate, currentMonth);
    });
  }, [displayTasks, currentMonth]);

  const { stats, isLoading: statsLoading } = useMultiPeriodFocusStats(currentMonth, completedTasks);
  const { fetchSummaryData } = useSummaryData();
  const { shouldRegenerateSummary, setCachedSummary, createFingerprint, getRelativeTime } = useAISummaryCache();
  const { statusesByDate, updateStatus, deleteStatus } = useStatusHistory(currentMonth);

  // Fetch time logged per task for the current month
  useEffect(() => {
    if (!user) return;
    
    const abortController = new AbortController();
    
    const fetchTaskTimes = async () => {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      
      const { data, error } = await supabase
        .from('focus_sessions')
        .select('task_id, duration_minutes')
        .eq('user_id', user.id)
        .gte('started_at', start.toISOString())
        .lte('started_at', end.toISOString())
        .abortSignal(abortController.signal);

      if (error) {
        if (error.message?.includes('aborted')) return;
        console.error('Error fetching task times:', error);
        return;
      }

      const timeMap: TaskTimeMap = {};
      data?.forEach((session) => {
        if (session.task_id) {
          timeMap[session.task_id] = (timeMap[session.task_id] || 0) + session.duration_minutes;
        }
      });
      setTaskTimeMap(timeMap);
    };

    fetchTaskTimes();
    
    return () => abortController.abort();
  }, [user, currentMonth]);

  const handleOpenLogTime = (task: Task) => {
    setSelectedTask(task);
    setShowLogTimeDialog(true);
  };

  useEffect(() => {
    if (!showLogTimeDialog || !user || !selectedTask) return;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    getFocusSessionsForTask(user.id, selectedTask.id, start, end).then((sessions) => {
      setLogTimeExistingSessions(sessions);
    });
  }, [showLogTimeDialog, user, selectedTask?.id]);

  const handleLogTime = async (minutes: number) => {
    if (!user || !selectedTask) return;

    const { error } = await saveFocusSession({
      userId: user.id,
      taskId: selectedTask.id,
      taskContent: selectedTask.content,
      workType: 'focus',
      timerMode: 'manager',
      durationMinutes: minutes,
      startedAt: new Date(),
    });

    if (error) {
      toast.error('Error logging time');
    } else {
      toast.success(`${minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`} logged for "${selectedTask.content}"`);
      setTaskTimeMap(prev => ({
        ...prev,
        [selectedTask.id]: (prev[selectedTask.id] || 0) + minutes
      }));
      setShowLogTimeDialog(false);
      setSelectedTask(null);
    }
  };

  const handleEditTime = async (sessionId: string, minutes: number) => {
    if (!user || !selectedTask) return;
    const { error } = await updateFocusSession(sessionId, { duration_minutes: minutes });
    if (error) {
      toast.error('Error updating time');
    } else {
      toast.success(`Updated to ${minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`}`);
      // Re-fetch task times for the month so stats are correct
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const { data } = await supabase
        .from('focus_sessions')
        .select('task_id, duration_minutes')
        .eq('user_id', user.id)
        .gte('started_at', start.toISOString())
        .lte('started_at', end.toISOString());
      const timeMap: TaskTimeMap = {};
      data?.forEach((session: { task_id: string | null; duration_minutes: number }) => {
        if (session.task_id) {
          timeMap[session.task_id] = (timeMap[session.task_id] || 0) + session.duration_minutes;
        }
      });
      setTaskTimeMap(prev => ({ ...prev, ...timeMap }));
      setShowLogTimeDialog(false);
      setSelectedTask(null);
    }
  };

  const tasksByDay = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    daysInMonth.forEach((day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      grouped[dateStr] = displayTasks
        .filter((t) => t.day === dateStr)
        .sort((a, b) => a.order_index - b.order_index);
    });
    return grouped;
  }, [displayTasks, daysInMonth]);

  const monthStats = useMemo(() => {
    const monthTasks = displayTasks.filter((t) => {
      const taskDate = new Date(t.day);
      return isSameMonth(taskDate, currentMonth);
    });
    const completed = monthTasks.filter((t) => t.completed).length;
    return { total: monthTasks.length, completed };
  }, [displayTasks, currentMonth]);

  const daysWithTasks = daysInMonth.filter((day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return (tasksByDay[dateStr] || []).length > 0;
  }).reverse(); // Most recent first

  const listParentRef = useRef<HTMLDivElement>(null);
  const [listScrollMargin, setListScrollMargin] = useState(0);

  const updateScrollMargin = useCallback(() => {
    if (listParentRef.current && daysWithTasks.length > 0) {
      setListScrollMargin(listParentRef.current.getBoundingClientRect().top + window.scrollY);
    }
  }, [daysWithTasks.length]);

  useEffect(() => {
    updateScrollMargin();
  }, [updateScrollMargin]);

  useEffect(() => {
    window.addEventListener('resize', updateScrollMargin);
    return () => window.removeEventListener('resize', updateScrollMargin);
  }, [updateScrollMargin]);

  const dayRowVirtualizer = useWindowVirtualizer({
    count: daysWithTasks.length,
    getScrollElement: () => window,
    estimateSize: (index) => {
      const dateStr = index < daysWithTasks.length ? format(daysWithTasks[index], 'yyyy-MM-dd') : '';
      return expandedDays.has(dateStr) ? 420 : 100;
    },
    scrollMargin: listScrollMargin,
    overscan: 3,
    getItemKey: (index) => format(daysWithTasks[index], 'yyyy-MM-dd'),
  });

  // Re-measure row heights when expand/collapse changes so cards don't overlap (skip mount to avoid scroll jump that hides top content)
  const isFirstMeasure = useRef(true);
  useEffect(() => {
    if (isFirstMeasure.current) {
      isFirstMeasure.current = false;
      return;
    }
    dayRowVirtualizer.measure();
  }, [expandedDays, dayRowVirtualizer]);

  const handleSelectPeriod = async (period: SummaryPeriod, regenerate = false) => {
    setSummaryPeriod(period);
    setShowPeriodPicker(false);
    setShowSummaryDrawer(true);
    setSummaryLoading(true);
    setSummaryText(null);
    setSummaryCachedAt(null);

    try {
      const summaryData = await fetchSummaryData(period);
      if (!summaryData) {
        throw new Error('Failed to fetch summary data');
      }

      setSummaryPeriodLabel(summaryData.periodLabel);

      // Check cache unless regenerate is forced
      if (!regenerate) {
        const { shouldRegenerate, cached } = shouldRegenerateSummary(
          period,
          summaryData.tasks,
          summaryData.sessions
        );

        if (!shouldRegenerate && cached) {
          // Use cached summary
          setSummaryText(cached.summary);
          setSummaryCachedAt(getRelativeTime(cached.generatedAt));
          setSummaryLoading(false);
          return;
        }
      }

      // Generate new summary
      const { data, error } = await supabase.functions.invoke('generate-week-summary', {
        body: summaryData,
      });

      if (error) throw error;
      
      if (data?.error) {
        throw new Error(data.error);
      }

      setSummaryText(data.summary);
      setSummaryCachedAt('Just now');

      // Cache the new summary
      const fingerprint = createFingerprint(summaryData.tasks, summaryData.sessions);
      setCachedSummary(period, data.summary, fingerprint, summaryData.periodLabel);
    } catch (err) {
      console.error('Error getting AI summary:', err);
      const message = err instanceof Error ? err.message : 'Failed to generate summary';
      toast.error(message);
      setShowSummaryDrawer(false);
    } finally {
      setSummaryLoading(false);
      setForceRegenerate(false);
    }
  };

  const handleRegenerate = () => {
    handleSelectPeriod(summaryPeriod, true);
  };

  const handleCardFlip = useCallback(() => {
    const currentIndex = PERIOD_CYCLE.indexOf(cardPeriod);
    const nextIndex = (currentIndex + 1) % PERIOD_CYCLE.length;
    setCardPeriod(PERIOD_CYCLE[nextIndex]);
  }, [cardPeriod]);

  const currentStats = stats?.[cardPeriod] ?? { totalMinutes: 0, topTask: null, fastestTask: null };

  return (
    <div className="space-y-6 scroll-mt-[calc(3.5rem+env(safe-area-inset-top,0px))]">
      {/* Header - scroll-margin keeps this visible below sticky nav when scrolling */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">History</h1>
          <p className="text-muted-foreground text-sm">Your achievements over time</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-32 text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </motion.div>

      {/* Stats + AI Summary — free users: only month numbers blurred, not day/week or whole section */}
      <div className="relative space-y-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-2 sm:gap-3"
          data-tour-target="history-progress"
        >
          <FlippableStatsCard
            currentPeriod={cardPeriod}
            isLoading={statsLoading}
            onClick={handleCardFlip}
            icon={<Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />}
            iconBgClass="bg-primary/10"
            renderContent={(period) => (
              <span className={cn(period === 'month' && !isProUser && 'blur-sm')}>
                <p className="text-lg sm:text-xl font-bold">{formatDuration(stats?.[period]?.totalMinutes ?? 0)}</p>
              </span>
            )}
          />
          <FlippableStatsCard
            currentPeriod={cardPeriod}
            isLoading={statsLoading}
            onClick={handleCardFlip}
            icon={<Timer className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent-foreground" />}
            iconBgClass="bg-accent/50"
            renderContent={(period) => {
              const topTask = stats?.[period]?.topTask ?? null;
              const content = topTask ? (
                <>
                  <p className="text-xs sm:text-sm font-semibold truncate" title={topTask.taskContent}>
                    {topTask.taskContent}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">{formatDuration(topTask.totalMinutes)} spent</p>
                </>
              ) : (
                <>
                  <p className="text-xs sm:text-sm text-muted-foreground">No data yet</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Start a session</p>
                </>
              );
              return <span className={cn(period === 'month' && !isProUser && 'blur-sm')}>{content}</span>;
            }}
          />
          <FlippableStatsCard
            currentPeriod={cardPeriod}
            isLoading={statsLoading}
            onClick={handleCardFlip}
            icon={<Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-success" />}
            iconBgClass="bg-success/10"
            renderContent={(period) => {
              const fastestTask = stats?.[period]?.fastestTask ?? null;
              const content = fastestTask ? (
                <>
                  <p className="text-xs sm:text-sm font-semibold truncate" title={fastestTask.taskContent}>
                    {fastestTask.taskContent}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Done in {formatDuration(fastestTask.durationMinutes)}</p>
                </>
              ) : (
                <>
                  <p className="text-xs sm:text-sm text-muted-foreground">No data yet</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Complete a task</p>
                </>
              );
              return <span className={cn(period === 'month' && !isProUser && 'blur-sm')}>{content}</span>;
            }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Popover open={showPeriodPicker} onOpenChange={(open) => { setShowPeriodPicker(open); }}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                disabled={monthStats.total === 0 && currentStats.totalMinutes === 0}
                data-tour-target="history-ai-summary"
                className={cn(
                  "w-full justify-start gap-2 border-border/50 task-shadow",
                  monthStats.total === 0 && currentStats.totalMinutes === 0
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-accent/50"
                )}
              >
                <Sparkles className="h-4 w-4 text-primary" />
                <span>Get AI Summary</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <p className="text-sm font-medium mb-3">Get insights for:</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start gap-2"
                  onClick={() => handleSelectPeriod('yesterday')}
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Yesterday
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start gap-2"
                  onClick={() => handleSelectPeriod('today')}
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start gap-2"
                  onClick={() => handleSelectPeriod('week')}
                >
                  <CalendarDays className="h-3.5 w-3.5" />
                  This Week
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start gap-2"
                  onClick={() => handleSelectPeriod('month')}
                >
                  <CalendarRange className="h-3.5 w-3.5" />
                  This Month
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </motion.div>

      </div>

      {/* Soft CTA for basic (non‑Pro) users: they see 7 days of data; invite to upgrade for more */}
      {!isProUser && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center justify-center gap-2 py-3 px-4 rounded-xl bg-primary/5 border border-primary/10"
        >
          <span className="text-sm text-muted-foreground">
            Showing last 7 days.
          </span>
          <button
            type="button"
            onClick={async () => {
              const result = await upgradeToPro();
              if (result?.checkoutUrl) {
                openPaymentUrl(result.checkoutUrl);
                return;
              }
              if (result?.error) toast.error(result.error);
              else if (result?.success) toast.success('Upgrade complete');
            }}
            className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
          >
            <Crown className="h-3.5 w-3.5" />
            View more stats by upgrading
          </button>
        </motion.div>
      )}

      {/* Month Summary */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="rounded-2xl border border-border/50 bg-card p-6 task-shadow"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center">
            <Trophy className="h-7 w-7 text-accent-foreground" />
          </div>
          <div>
            <p className="text-3xl font-bold">{monthStats.completed}</p>
            <p className="text-sm text-muted-foreground">
              tasks completed this month
              {monthStats.total > 0 && (
                <span className="ml-1">
                  ({Math.round((monthStats.completed / monthStats.total) * 100)}% of {monthStats.total})
                </span>
              )}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Days List (virtualized for 500+ tasks) */}
      {daysWithTasks.length > 0 ? (
        <div ref={listParentRef} className="space-y-4">
          <div
            style={{
              height: `${dayRowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {dayRowVirtualizer.getVirtualItems().map((virtualRow) => {
              const day = daysWithTasks[virtualRow.index];
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayTasks = tasksByDay[dateStr] || [];
              const completedDayTasks = dayTasks.filter((t) => t.completed);
              const isDayExpanded = expandedDays.has(dateStr);

              return (
                <div
                  key={virtualRow.key}
                  ref={dayRowVirtualizer.measureElement}
                  data-index={virtualRow.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: `translateY(${virtualRow.start - dayRowVirtualizer.options.scrollMargin}px)`,
                  }}
                  className="pb-4"
                >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="rounded-xl border border-border/50 bg-card overflow-hidden task-shadow"
              >
                <Collapsible open={isDayExpanded} onOpenChange={() => toggleDayExpanded(dateStr)}>
                  {/* Day Header - Clickable */}
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 border-b border-border/50 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <ChevronDown 
                          className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform duration-200",
                            !isDayExpanded && "-rotate-90"
                          )} 
                        />
                        <div className="w-10 h-10 rounded-xl bg-background flex flex-col items-center justify-center border border-border/50">
                          <span className="text-xs text-muted-foreground font-medium leading-none">
                            {format(day, 'EEE')}
                          </span>
                          <span className="text-sm font-bold leading-none mt-0.5">
                            {format(day, 'd')}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {isToday(day) ? 'Today' : format(day, 'EEEE')}
                          </p>
                          <p className="text-xs text-muted-foreground">{format(day, 'MMMM d, yyyy')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{completedDayTasks.length}/{dayTasks.length}</p>
                        <p className="text-xs text-muted-foreground">completed</p>
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    {/* Daily Notes */}
                    <DailyNotesSection
                      status={statusesByDate[dateStr] || null}
                      onUpdate={updateStatus}
                      onDelete={deleteStatus}
                    />

                    {/* Tasks */}
                    <div className="p-3 space-y-2">
                      {dayTasks.map((task) => {
                        const loggedTime = taskTimeMap[task.id] || 0;
                        
                        return (
                          <div
                            key={task.id}
                            className={cn(
                              "group flex items-center gap-3 p-2 rounded-lg",
                              task.completed ? "bg-success/5" : "bg-muted/30"
                            )}
                          >
                            <div
                              className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                task.completed
                                  ? "bg-success border-success text-success-foreground"
                                  : "border-muted-foreground/30"
                              )}
                            >
                              {task.completed && <Check className="h-3 w-3" />}
                            </div>
                            <span className={cn(
                              "text-sm flex-1",
                              task.completed && "text-muted-foreground"
                            )}>
                              {task.content}
                            </span>
                            
                            {/* Logged time display */}
                            {loggedTime > 0 && (
                              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                {formatDuration(loggedTime)}
                              </span>
                            )}
                            
                            {/* Log time button */}
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenLogTime(task);
                                    }}
                                    className={cn(
                                      "h-6 w-6 text-muted-foreground/50 hover:text-primary",
                                      "opacity-0 group-hover:opacity-100 transition-opacity"
                                    )}
                                  >
                                    <Timer className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Log time</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="w-12 h-12 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <Calendar className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">No completed tasks yet. Finish your first task to see it here.</p>
        </motion.div>
      )}

      {/* AI Summary Drawer */}
      <AISummaryDrawer
        open={showSummaryDrawer}
        onOpenChange={setShowSummaryDrawer}
        summary={summaryText}
        isLoading={summaryLoading}
        period={summaryPeriod}
        periodLabel={summaryPeriodLabel}
      />

      {/* Log Time Dialog */}
      <LogTimeDialog
        open={showLogTimeDialog}
        onOpenChange={(open) => {
          setShowLogTimeDialog(open);
          if (!open) setSelectedTask(null);
        }}
        onLog={handleLogTime}
        onEdit={handleEditTime}
        taskName={selectedTask?.content || ''}
        existingSessions={logTimeExistingSessions}
      />
    </div>
  );
}
